'use client';

import { ChefHat, Home, Search } from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import { Footer } from '@/features/landing/components/layout/footer';
import { LandingNavbar } from '@/features/landing/components/navbar';

export default function NotFound() {
  const t = useTranslations('common.notFound');

  return (
    <div className="min-h-safe-screen flex flex-col">
      <LandingNavbar />

      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-20 pb-8">
        <div className="max-w-2xl mx-auto text-center">
          {/* Fun animated chef hat icon */}
          <div className="relative mb-6">
            <div className="animate-bounce">
              <ChefHat className="w-16 h-16 sm:w-24 sm:h-24 mx-auto text-primary" />
            </div>
            {/* Floating dots animation */}
            <div className="absolute -top-4 -left-4 w-3 h-3 bg-primary/60 rounded-full animate-ping"></div>
            <div className="absolute -top-2 -right-6 w-2 h-2 bg-primary/40 rounded-full animate-ping animation-delay-200"></div>
            <div className="absolute -bottom-2 -left-2 w-2 h-2 bg-primary/50 rounded-full animate-ping animation-delay-500"></div>
          </div>

          {/* Error code with fun styling */}
          <div className="mb-6">
            <h1 className="text-6xl sm:text-8xl font-bold text-primary animate-pulse">404</h1>
            <div className="text-xl sm:text-2xl font-semibold text-muted-foreground mt-2">
              {t('title')}
            </div>
          </div>

          {/* Friendly message */}
          <div className="mb-8 space-y-3">
            <p className="text-base sm:text-lg text-muted-foreground">{t('description')}</p>
            <p className="text-sm sm:text-base text-muted-foreground max-w-md mx-auto">
              {t('details')}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link href="/" className="flex items-center gap-2">
                <Home className="w-4 h-4" />
                {t('actions.backToHome')}
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link href="/app" className="flex items-center gap-2">
                <Search className="w-4 h-4" />
                {t('actions.explore')}
              </Link>
            </Button>
          </div>

          {/* Fun additional elements */}
          <div className="mt-12 text-sm text-muted-foreground">
            <p>{t('errorCode')}</p>
            <p className="mt-1">{t('funMessage')}</p>
          </div>
        </div>
      </main>

      <Footer />

      <style jsx>{`
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        .animation-delay-500 {
          animation-delay: 500ms;
        }
      `}</style>
    </div>
  );
}
